<template>
  <b-modal
    :visible="visible"
    centered
    modal-class="status-update-modal sts_edit_modal_sp iu-modal"
    no-close-on-backdrop
    title="Edit Discounts"
    @change="val => $emit('update:visible', val)"
  >
    <div class="d-flex mb-50 sts_item_header_ts">
      <div class="flex-grow-1 mr-1 font-weight-bold">
        Item
      </div>
      <div
        class="font-weight-bold discount-amount-col"
      >
        Discount Price
      </div>
    </div>

    <draggable
      v-model="rows"
      handle=".drag-handle"
      tag="div"
    >
      <div
        v-for="(row, index) in rows"
        :key="row.key"
        class="d-flex align-items-center mb-1 sts_item_content_ts"
      >
        <div class="flex-grow-1 mr-1 sts_input_sp">
          <b-form-input
            v-if="row.locked"
            :value="displayName(row)"
            disabled
          />
          <div
            v-else
            class="position-relative"
          >
            <b-form-input
              v-model="row.searchQuery"
              placeholder="Search Voucher Name, or Discount Code"
              autocomplete="off"
              @input="onRowSearchInput(row)"
              @focusout="hideRowResultsDelayed(row)"
            />
            <div
              v-show="row.showResults"
              class="search-results mt-50 z-index-99"
            >
              <b-list-group
                v-if="row.searchResults.length"
                flush
              >
                <b-list-group-item
                  v-for="promo in row.searchResults"
                  :key="promo._id"
                  class="cursor-pointer"
                  @click="selectRowPromotion(row, promo)"
                >
                  <span class="text-primary">{{ promo.name }}</span>
                  <br>
                  <small class="text-muted">{{ promo.couponCode || promo.label }}</small>
                </b-list-group-item>
              </b-list-group>
              <b-list-group v-else-if="row.searchQuery">
                <b-list-group-item>
                  No result found.
                </b-list-group-item>
              </b-list-group>
            </div>
          </div>
        </div>
        <b-input-group
          prepend="$"
          class="discount-amount-col mr-1"
        >
          <b-form-input
            :value="numberFormat(row.amount)"
            disabled
          />
        </b-input-group>
        <feather-icon
          icon="MenuIcon"
          class="drag-handle cursor-move text-muted mr-1"
          size="18"
        />
        <feather-icon
          icon="Trash2Icon"
          class="cursor-pointer text-danger"
          size="18"
          @click="removeRow(index)"
        />
      </div>
    </draggable>

    <template #modal-footer="{}">
      <div class="d-flex justify-content-between w-100">
        <b-button
          variant="primary"
          type="button"
          :disabled="saving"
          @click="addRow"
        >
          <span class="align-middle">Add Discount</span>
        </b-button>
        <div>
          <b-button
            variant="flat-primary"
            class="mr-50"
            :disabled="saving"
            @click="$emit('update:visible', false)"
          >
            Cancel
          </b-button>
          <b-button
            variant="success-custom"
            type="button"
            :disabled="saving"
            @click="save"
          >
            <feather-icon
              icon="SaveIcon"
              class="mr-50"
              size="16"
            />
            <span class="align-middle">Save Changes</span>
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BFormInput, BInputGroup, BButton, BListGroup, BListGroupItem,
} from 'bootstrap-vue'
import draggable from 'vuedraggable'
import axios from '@axios'

export default {
  components: {
    BModal, BFormInput, BInputGroup, BButton, BListGroup, BListGroupItem, draggable,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    // Existing applied promotions - each { _id, promotion, name, couponCode, amount, discountType, discountValue }.
    promotions: {
      type: Array,
      default: () => [],
    },
    // Used only for a client-side preview amount on a newly-selected (not-yet-saved) row - the
    // authoritative amount always comes back from the server on save.
    productPrice: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      rows: [],
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.rows = this.promotions.map(promotion => ({
          key: promotion._id,
          promotionId: promotion.promotion,
          name: promotion.name,
          couponCode: promotion.couponCode,
          amount: promotion.amount,
          discountType: promotion.discountType,
          discountValue: promotion.discountValue,
          locked: true,
          searchQuery: '',
          searchResults: [],
          showResults: false,
          timer: null,
        }))
      }
    },
  },
  methods: {
    displayName(row) {
      return row.couponCode ? `${row.name} - ${row.couponCode}` : `${row.name} - Auto applied`
    },
    addRow() {
      this.rows.push({
        key: `new-${Date.now()}-${Math.random()}`,
        promotionId: null,
        name: '',
        couponCode: '',
        amount: 0,
        discountType: null,
        discountValue: 0,
        locked: false,
        searchQuery: '',
        searchResults: [],
        showResults: false,
        timer: null,
      })
    },
    removeRow(index) {
      this.rows.splice(index, 1)
    },
    onRowSearchInput(row) {
      if (row.timer) clearTimeout(row.timer)
      if (!row.searchQuery || row.searchQuery.length < 2) {
        Object.assign(row, { searchResults: [], showResults: false })
        return
      }
      Object.assign(row, {
        timer: setTimeout(() => {
          axios.get('reservation/promotions', { params: { search: row.searchQuery, perPage: 10 } })
            .then(response => {
              const chosenIds = this.rows.filter(r => r !== row && r.promotionId).map(r => r.promotionId)
              const results = (response.data.promotions || []).filter(promo => !chosenIds.includes(promo._id))
              Object.assign(row, { searchResults: results, showResults: true })
            })
        }, 1000),
      })
    },
    hideRowResultsDelayed(row) {
      setTimeout(() => {
        Object.assign(row, { showResults: false })
      }, 200)
    },
    // Preview only - a rough client-side estimate so the row isn't left showing $0.00 before
    // save. The real amount (incl. maxDiscountValue capping) always comes from the server, which
    // recomputes every row's amount sequentially in final order (see computeSequentialAmounts in
    // helpers/promotion.js) - a later promotion discounts what's left after earlier ones in the
    // list, not the full price, so this preview mirrors that using rows ahead of this one.
    previewAmount(promo, remainingBase) {
      if (promo.discountType === 'Singapore Dollars ($)') return promo.discountValue
      if (promo.discountType === 'Percentage (%)') return (Number(remainingBase) || 0) * (promo.discountValue / 100)
      return 0
    },
    selectRowPromotion(row, promo) {
      const rowIndex = this.rows.indexOf(row)
      const priorAmount = this.rows.slice(0, rowIndex).reduce((sum, r) => sum + (Number(r.amount) || 0), 0)
      const remainingBase = Math.max((Number(this.productPrice) || 0) - priorAmount, 0)
      Object.assign(row, {
        promotionId: promo._id,
        name: promo.name,
        couponCode: promo.couponCode,
        discountType: promo.discountType,
        discountValue: promo.discountValue,
        amount: this.previewAmount(promo, remainingBase),
        locked: true,
        searchQuery: promo.name,
        showResults: false,
      })
    },
    save() {
      this.$emit('save', {
        promotionIds: this.rows.filter(row => row.promotionId).map(row => row.promotionId),
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.discount-amount-col {
  width: 140px;
  flex: 0 0 140px;
}
.search-results {
  position: absolute;
  background: #fff;
  border: 1px solid #e0e5eb;
  border-radius: 6px;
  width: 100%;
  max-height: 220px;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
